import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import type {
    CalendarBlockedDate,
    CalendarBooking,
    CalendarCleaning,
    CalendarEvent,
    DateSelection,
    MissingCleaning,
} from '../types/calendar';
import CalendarDay from './CalendarDay';

interface CalendarGridProps {
    month: number; // 1-12
    year: number;
    weekStartDay: 0 | 1; // 0 = Sunday, 1 = Monday
    bookings: CalendarBooking[];
    blockedDates: CalendarBlockedDate[];
    cleanings: CalendarCleaning[];
    missingCleanings: MissingCleaning[];
    selectedRange: DateSelection;
    onDayClick: (date: string) => void;
    onBookingClick: (booking: CalendarBooking, anchorEl: HTMLElement) => void;
    onBlockedDateClick: (blockedDate: CalendarBlockedDate) => void;
    onCleaningClick: (cleaning: CalendarCleaning) => void;
    onMissingCleaningClick: (missingCleaning: MissingCleaning) => void;
}

const WEEKDAYS_SUNDAY_START = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_MONDAY_START = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const CalendarGrid = ({
    month,
    year,
    weekStartDay,
    bookings,
    blockedDates,
    cleanings,
    missingCleanings,
    selectedRange,
    onDayClick,
    onBookingClick,
    onBlockedDateClick,
    onCleaningClick,
    onMissingCleaningClick,
}: CalendarGridProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const weekdays = weekStartDay === 1 ? WEEKDAYS_MONDAY_START : WEEKDAYS_SUNDAY_START;

    // Calculate calendar days (6 weeks to cover all month layouts)
    const calendarDays = useMemo(() => {
        const firstOfMonth = DateTime.local(year, month, 1);
        // Get day of week (1=Monday, 7=Sunday in Luxon)
        const firstDayOfWeek = firstOfMonth.weekday; // 1-7 (Mon-Sun)

        // Calculate days to go back to reach the start of the week
        // For Monday start (weekStartDay=1): Monday is 1, so go back (firstDayOfWeek - 1) days
        // For Sunday start (weekStartDay=0): Sunday is 7, so go back (firstDayOfWeek % 7) days
        const daysToGoBack =
            weekStartDay === 1
                ? firstDayOfWeek - 1
                : firstDayOfWeek % 7;

        const startDay = firstOfMonth.minus({ days: daysToGoBack });
        const days: DateTime[] = [];

        // Generate 6 weeks of days (42 days)
        for (let i = 0; i < 42; i++) {
            days.push(startDay.plus({ days: i }));
        }

        return days;
    }, [month, year, weekStartDay]);

    // Convert bookings and blocked dates to events
    const events: CalendarEvent[] = useMemo(() => {
        const result: CalendarEvent[] = [];

        // Add bookings
        bookings.forEach((booking) => {
            // Booking spans from checkin to checkout (inclusive)
            result.push({
                id: booking.id,
                type: 'booking',
                startDate: booking.checkin,
                endDate: booking.checkout,
                label: booking.guestName,
                booking,
            });
        });

        // Add blocked dates
        blockedDates.forEach((blocked) => {
            result.push({
                id: blocked.id,
                type:
                    blocked.blockType === 'maintenance'
                        ? 'blocked-maintenance'
                        : 'blocked-personal',
                startDate: blocked.startDate,
                endDate: blocked.endDate,
                label:
                    blocked.blockType === 'maintenance'
                        ? 'Maintenance'
                        : blocked.reason ?? 'Personal',
                blockedDate: blocked,
            });
        });

        // Add cleanings (single-day events)
        cleanings.forEach((cleaning) => {
            result.push({
                id: cleaning.id,
                type: cleaning.isPaid ? 'cleaning' : 'cleaning-unpaid',
                startDate: cleaning.date,
                endDate: cleaning.date,
                label: cleaning.isPaid ? `Cleaning ${cleaning.time}` : `Unpaid ${cleaning.time}`,
                cleaning,
            });
        });

        // Add missing cleanings (single-day events)
        // When suggestedDate is available (avoidSundays enabled + Sunday checkout),
        // show indicator on the suggested Monday instead of the Sunday checkout
        missingCleanings.forEach((missing, index) => {
            const displayDate = missing.suggestedDate ?? missing.checkoutDate;
            result.push({
                id: -(index + 1), // Use negative IDs to avoid conflicts
                type: 'missing-cleaning',
                startDate: displayDate,
                endDate: displayDate,
                label: 'No cleaning',
                missingCleaning: missing,
            });
        });

        return result;
    }, [bookings, blockedDates, cleanings, missingCleanings]);

    // Check if a date is selected
    const isDateSelected = (dateStr: string): boolean => {
        if (!selectedRange.startDate) return false;
        if (!selectedRange.endDate) return dateStr === selectedRange.startDate;

        return dateStr >= selectedRange.startDate && dateStr <= selectedRange.endDate;
    };

    const today = DateTime.now().toFormat('yyyy-MM-dd');

    return (
        <Box
            sx={{
                border: '3px solid #374151',
                backgroundColor: '#0f0f23',
                overflow: 'hidden',
            }}
        >
            {/* Weekday Headers */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    borderBottom: '3px solid #6366f1',
                    backgroundColor: '#1a1a2e',
                }}
            >
                {weekdays.map((day) => (
                    <Box
                        key={day}
                        sx={{
                            p: 1,
                            textAlign: 'center',
                            borderRight: '1px solid #374151',
                            '&:last-child': {
                                borderRight: 'none',
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: isMobile ? '0.7rem' : '0.85rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                color: '#9ca3af',
                            }}
                        >
                            {isMobile ? day.charAt(0) : day}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Calendar Grid */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    overflow: 'hidden',
                }}
            >
                {calendarDays.map((date) => {
                    const dateStr = date.toFormat('yyyy-MM-dd');
                    const isCurrentMonth = date.month === month;
                    const isToday = dateStr === today;
                    const isSelected = isDateSelected(dateStr);

                    return (
                        <CalendarDay
                            key={dateStr}
                            date={date}
                            isCurrentMonth={isCurrentMonth}
                            isToday={isToday}
                            isSelected={isSelected}
                            isMobile={isMobile}
                            events={events}
                            onDayClick={onDayClick}
                            onBookingClick={onBookingClick}
                            onBlockedDateClick={onBlockedDateClick}
                            onCleaningClick={onCleaningClick}
                            onMissingCleaningClick={onMissingCleaningClick}
                        />
                    );
                })}
            </Box>
        </Box>
    );
};

export default CalendarGrid;
